import React, { useState, useEffect, useRef } from 'react';
import AWSIcon from './AWSIcon';
import './CodeTypingDemo.css';
import hljs from 'highlight.js/lib/core';
import 'highlight.js/styles/atom-one-dark.css'; // Import a theme for highlight.js
import python from 'highlight.js/lib/languages/python';

const CodeTypingDemo = () => {
    const [currentCode, setCurrentCode] = useState('');
    const [currentService, setCurrentService] = useState(null);
    const [isTyping, setIsTyping] = useState(true);
    const [shouldHighlight, setShouldHighlight] = useState(false);
    const snippetIndexRef = useRef(0);
    const charIndexRef = useRef(0);
    const currentTextRef = useRef('');
    

    const codeSnippets = [
        { 
            service: 'lambda',
            code: `import boto3

lambda_client = boto3.client('lambda')
response = lambda_client.invoke(
    FunctionName='my-function',
    InvocationType='RequestResponse',
    Payload=json.dumps({'key': 'value'})
)`
        },
        {
            service: 'emr',
            code: `import boto3

emr_client = boto3.client('emr')
cluster_config = {
    'Name': 'MyEMRCluster',
    'ReleaseLabel': 'emr-6.3.0',
    'Instances': {
        'InstanceGroups': [...],
        'AutoScaling': True
    }
}`
        },
        {
            service: 'glue',
            code: `import boto3

glue_client = boto3.client('glue')
job_run = glue_client.start_job_run(
    JobName='my-etl-job',
    Arguments={
        '--input-path': 's3://my-bucket/input/',
        '--output-path': 's3://my-bucket/output/'
    }
)`
        },
        {
            service: 'airflow',
            code: `from airflow import DAG
from airflow.operators.bash import BashOperator
from datetime import datetime

with DAG(
    dag_id='daily_etl_pipeline',
    start_date=datetime(2023, 1, 1),
    schedule_interval='@daily'
) as dag:
    run_glue_job = BashOperator(
        task_id='run_glue_job',
        bash_command='aws glue start-job-run ...'
    )`
        },
        {
            service: 'emr-spark',
            code: `from pyspark.sql import SparkSession

# Initialize Spark Session
spark = SparkSession.builder.appName("DataProcessing").getOrCreate()

# Read data from S3
df = spark.read.parquet("s3://my-bucket/raw_data/")

df_transformed = df.filter(df.status == "completed")
df_transformed.write.mode("overwrite").parquet("s3://my-bucket/processed_data/")`
        },
        {
            service: 'kinesis',
            code: `import boto3
import json

kinesis_client = boto3.client('kinesis')

response = kinesis_client.put_record(
    StreamName='live-event-stream',
    Data=json.dumps({'user_id': 123, 'event': 'login'}),
    PartitionKey='user_id'
)`
        },
        {
            service: 'athena',
            code: `SELECT
    user_id,
    product_name,
    COUNT(*) as purchase_count
FROM "my_database"."orders_table"
WHERE event_date >= DATE('2023-01-01')
GROUP BY 1, 2
ORDER BY 3 DESC
LIMIT 10;`
        },
        {
            service: 'snowflake',
            code: `CREATE OR REPLACE TABLE sales_summary AS
SELECT
    product_id,
    MONTH(sale_date) as sale_month,
    SUM(amount) as total_sales
FROM raw_sales
GROUP BY 1, 2
ORDER BY sale_month, total_sales DESC;`
        }
    ];

    hljs.registerLanguage('python', python);

    const highlightCode = (code) => {
        if (!code) return '';
        // Use the library to highlight the code and get the HTML string
        return hljs.highlight(code, { language: 'python' }).value;
    };

    // const highlightCode = (code) => {
    //     if (!code) return '';
        
    //     return code
    //         .replace(/\b(import|from|def|return|class|if|else|for|while|True|False)\b/g, '<span class="keywords">$&</span>')
    //         .replace(/'[^']*'|"[^"]*"/g, '<span class="string">$&</span>')
    //         .replace(/#[^\n]*/g, '<span class="comment">$&</span>')
    //         .replace(/\b(boto3|client|invoke|start_job_run|json|dumps)\b/g, '<span class="function">$&</span>');
    // };

    // useEffect(() => {
    //     let intervalId;
    //     let timeoutId1;
    //     let timeoutId2;
        
    //     const startNewSnippet = () => {
    //         // Reset everything for a new snippet
    //         charIndexRef.current = 0;
    //         currentTextRef.current = '';
    //         setCurrentCode('');
    //         setShouldHighlight(false);
    //         setIsTyping(true);
    //         setCurrentService(codeSnippets[snippetIndexRef.current].service);
    //     };

    //     const typeCode = () => {
    //         const snippet = codeSnippets[snippetIndexRef.current];
            
    //         if (charIndexRef.current < snippet.code.length) {
    //             currentTextRef.current += snippet.code[charIndexRef.current];
    //             setCurrentCode(currentTextRef.current);
    //             charIndexRef.current++;
    //         } else {
    //             // Typing complete
    //             setIsTyping(false);
    //             setShouldHighlight(true); // Enable highlighting only after typing is done
                
    //             // Wait before moving to next snippet
    //             timeoutId1 = setTimeout(() => {
    //                 setCurrentService(null);
    //                 timeoutId2 = setTimeout(() => {
    //                     snippetIndexRef.current = (snippetIndexRef.current + 1) % codeSnippets.length;
    //                     startNewSnippet();
    //                 }, 500);
    //             }, 3000);
    //         }
    //     };

    //     // Start the first snippet
    //     startNewSnippet();
        
    //     intervalId = setInterval(typeCode, 40);
        
    //     return () => {
    //         clearInterval(intervalId);
    //         clearTimeout(timeoutId1);
    //         clearTimeout(timeoutId2);
    //     };
    // }, []);

    useEffect(() => {
        let typingInterval;
        let isMounted = true;

        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
        
        const startNewSnippet = () => {
            // Reset everything for a new snippet
            charIndexRef.current = 0;
            currentTextRef.current = '';
            setCurrentCode('');
            setShouldHighlight(false);
            setIsTyping(true);
            setCurrentService(codeSnippets[snippetIndexRef.current].service);
            typingInterval = setInterval(typeCode, 40);
        };

        const typeCode = async () => {
            const snippet = codeSnippets[snippetIndexRef.current];
            
            if (charIndexRef.current < snippet.code.length) {
                currentTextRef.current += snippet.code[charIndexRef.current];
                setCurrentCode(currentTextRef.current);
                charIndexRef.current++;
            } else {
                // Stop the current typing interval
                clearInterval(typingInterval);

                // Typing complete
                setIsTyping(false);
                setShouldHighlight(true);

                await delay(3000);
                if (!isMounted) return;

                setCurrentService(null);
                await delay(500);
                if (!isMounted) return;

                snippetIndexRef.current = (snippetIndexRef.current + 1) % codeSnippets.length;
                startNewSnippet();
            }
        };

        // Start the first snippet
        startNewSnippet();
        
        return () => {
            isMounted = false;
            clearInterval(typingInterval);
        };
    }, []);

    return (
        <div className="code-typing-demo">
            {currentService && (
                <div className="aws-icon-container">
                    <AWSIcon service={currentService} isVisible={!!currentService} />
                </div>
            )}
            <pre className="code-block">
                <code dangerouslySetInnerHTML={{ __html: highlightCode(currentCode) }} />
                {/* {shouldHighlight ? (
                    <code dangerouslySetInnerHTML={{ __html: highlightCode(currentCode) }} />
                ) : (
                    <code>{currentCode}</code>
                )} */}
                {/* <code>{currentCode}</code> */}
                {isTyping && <span className="cursor">▋</span>}
            </pre>
        </div>
    );
};

export default CodeTypingDemo;